import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, Ticket } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { formatError } from '../lib/api';
import { toast } from 'sonner';

export default function Register() {
  const [params] = useSearchParams();
  const initialCode = params.get('code') || '';
  const [tab, setTab] = useState(initialCode ? 'invite' : 'create');
  const { registerCreateWorkspace, registerWithInvite } = useAuth();
  const navigate = useNavigate();

  const [createForm, setCreateForm] = useState({ workspace_name: '', name: '', email: '', password: '' });
  const [inviteForm, setInviteForm] = useState({ invite_code: initialCode, name: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);

  const submitCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await registerCreateWorkspace(createForm);
      toast.success(`Workspace "${createForm.workspace_name}" created`);
      navigate('/');
    } catch (err) { toast.error(formatError(err)); }
    finally { setLoading(false); }
  };

  const submitInvite = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...inviteForm, invite_code: inviteForm.invite_code.toUpperCase() };
      await registerWithInvite(payload);
      toast.success('Welcome to the team');
      navigate('/');
    } catch (err) { toast.error(formatError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid-pattern bg-[#F4F4F5] flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white border border-zinc-900 shadow-brutal p-6 sm:p-8">
        <div className="mb-6">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">REGISTER</div>
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">Create account</h2>
        </div>

        <div className="grid grid-cols-2 border border-zinc-900 mb-6">
          <button
            onClick={() => setTab('create')}
            data-testid="tab-create-workspace"
            className={`flex items-center justify-center gap-2 px-3 py-2.5 text-[10px] font-bold uppercase tracking-[0.15em] transition-colors ${tab === 'create' ? 'bg-zinc-900 text-white' : 'bg-white hover:bg-zinc-100'}`}
          >
            <Building2 size={12} /> New Workspace
          </button>
          <button
            onClick={() => setTab('invite')}
            data-testid="tab-use-invite"
            className={`flex items-center justify-center gap-2 px-3 py-2.5 text-[10px] font-bold uppercase tracking-[0.15em] transition-colors ${tab === 'invite' ? 'bg-zinc-900 text-white' : 'bg-white hover:bg-zinc-100'}`}
          >
            <Ticket size={12} /> Use Invite Code
          </button>
        </div>

        {tab === 'create' ? (
          <form onSubmit={submitCreate} className="space-y-4">
            <p className="text-xs text-zinc-600">Start a brand new workspace for your company. You&apos;ll be the boss.</p>
            <Field label="Company / Workspace name" testid="ws-name" value={createForm.workspace_name} onChange={(v) => setCreateForm({ ...createForm, workspace_name: v })} />
            <Field label="Your full name" testid="reg-name" value={createForm.name} onChange={(v) => setCreateForm({ ...createForm, name: v })} />
            <Field label="Email" type="email" testid="reg-email" value={createForm.email} onChange={(v) => setCreateForm({ ...createForm, email: v })} />
            <Field label="Password" type="password" testid="reg-password" value={createForm.password} onChange={(v) => setCreateForm({ ...createForm, password: v })} />
            <button data-testid="create-ws-submit" type="submit" disabled={loading} className="w-full bg-[#002FA7] text-white py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#002088] disabled:opacity-60 transition-colors">
              {loading ? 'Creating...' : 'Create Workspace →'}
            </button>
          </form>
        ) : (
          <form onSubmit={submitInvite} className="space-y-4">
            <p className="text-xs text-zinc-600">Got an invite code or link from your boss/manager? Use it here.</p>
            <Field label="Invite code" testid="invite-code" value={inviteForm.invite_code} onChange={(v) => setInviteForm({ ...inviteForm, invite_code: v.toUpperCase() })} placeholder="e.g., FOX-7234" mono />
            <Field label="Your full name" testid="reg-name" value={inviteForm.name} onChange={(v) => setInviteForm({ ...inviteForm, name: v })} />
            <Field label="Email" type="email" testid="reg-email" value={inviteForm.email} onChange={(v) => setInviteForm({ ...inviteForm, email: v })} />
            <Field label="Password" type="password" testid="reg-password" value={inviteForm.password} onChange={(v) => setInviteForm({ ...inviteForm, password: v })} />
            <button data-testid="invite-submit" type="submit" disabled={loading} className="w-full bg-[#002FA7] text-white py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#002088] disabled:opacity-60 transition-colors">
              {loading ? 'Joining...' : 'Join Workspace →'}
            </button>
          </form>
        )}

        <div className="mt-6 pt-5 border-t border-zinc-200 text-sm text-zinc-600">
          Already have an account?{' '}
          <Link to="/login" data-testid="to-login" className="font-bold text-[#002FA7] underline decoration-2 underline-offset-2">Sign in</Link>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, value, onChange, type = 'text', placeholder, mono = false }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-2">{label}</label>
      <input
        data-testid={testid}
        type={type}
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full border border-zinc-300 focus:border-[#002FA7] outline-none px-3 py-3 bg-white ${mono ? 'font-mono tracking-wider' : ''}`}
      />
    </div>
  );
}
